import {
  FormattedCurrencyValue,
  FormattedValue,
} from "../types/FormattedValues";
import {
  NetQuoteInclusions,
  payCyclePeriodOptions,
  Quote,
  QuoteFormatted,
  QuoteInclusionsFormatted,
  QuotePayPeriod,
} from "../types/Quote";
import { getAutoCompleteLabel } from "./buildOptions";
import { toDollars, toFormattedNumber } from "./currencies";
import { getPurchaseTypeLabel } from "./quotes";

export const toFormattedValue = <T>(
  raw: T,
  formatted: string
): FormattedValue<T> => {
  return {
    raw,
    formatted,
  };
};

export const toFormattedCurrency = (curr: number): FormattedCurrencyValue => {
  return {
    raw: curr,
    formatted: toDollars(curr, 2),
    formatted_exc_cents: toDollars(curr, 0),
  };
};

const formatQuoteInclusions = (
  inclusions: NetQuoteInclusions,
  payPeriod: QuotePayPeriod
): QuoteInclusionsFormatted => {
  return {
    finance: toFormattedCurrency(inclusions.finance[payPeriod]),
    servicing: toFormattedCurrency(inclusions.servicing[payPeriod]),
    tyres: toFormattedCurrency(inclusions.tyres[payPeriod]),
    fuel: toFormattedCurrency(inclusions.fuel[payPeriod]),
    rego: toFormattedCurrency(inclusions.rego[payPeriod]),
    insurance: toFormattedCurrency(inclusions.insurance[payPeriod]),
    managementFees: toFormattedCurrency(inclusions.managementFees[payPeriod]),
    luxuryCharge: toFormattedCurrency(inclusions.luxuryCharge[payPeriod]),
    gst: toFormattedCurrency(inclusions.gst[payPeriod]),
  };
};

export const formatQuote = (
  quote: Quote,
  payCyclePeriod?: QuotePayPeriod
): QuoteFormatted => {
  const period = payCyclePeriod || quote.term.payPeriod;
  const periodLabel = getAutoCompleteLabel(payCyclePeriodOptions, period);

  return {
    id: quote.identifiers.crmQuoteId,
    dossierId: quote.identifiers.crmDossierId,
    opportunityId: quote.identifiers.crmOpportunityId,
    name: quote.identifiers.name || "",
    isLocked: quote.config.isLocked,
    isVehicleConfigurationLocked: quote.config.isVehicleConfigurationLocked,
    vehicle: {
      make: quote.vehicle.make,
      makeId: quote.vehicle.makeId,
      model: quote.vehicle.model,
      modelId: quote.vehicle.modelId,
      year: quote.vehicle.year,
      description: quote.vehicle.variantName,
      variantId: quote.vehicle.variantId,
      variantCode: quote.vehicle.variantCode,
      initialOdometer: quote.vehicle.initialOdometer
        ? toFormattedValue(
            quote.vehicle.initialOdometer,
            `${quote.vehicle.initialOdometer}`
          )
        : null,
    },
    state: `${quote.vehicle.state}`,
    packageValue: toFormattedCurrency(quote.salaryComparison.grossCost[period]),
    netCost: toFormattedCurrency(quote.salaryComparison.netCost[period]),
    inclusions: formatQuoteInclusions(quote.inclusions, period),
    payPeriod: toFormattedValue(period, periodLabel),
    runningCostsPA: toFormattedCurrency(
      quote.salaryComparison.runningCosts.annual
    ),
    annualSalary: toFormattedCurrency(quote.salaryComparison.salaryExSuper),
    annualKm: toFormattedValue(
      quote.vehicle.budgetedAnnualKm,
      `${toFormattedNumber(quote.vehicle.budgetedAnnualKm, 0)} km`
    ),
    vehicleOnroadCosts: toFormattedCurrency(quote.vehicle.onroadCosts),
    vehicleResidualGross: toFormattedCurrency(quote.vehicle.residual.incGst),
    term: toFormattedValue(quote.term.termMonths, `${quote.term.termMonths}`),
    businessUsage: toFormattedValue(
      quote.vehicle.businessUsePercentage,
      `${quote.vehicle.businessUsePercentage.toFixed(0)}%`
    ),
    savings: toFormattedValue(
      quote.salaryComparison.savingsPercentage,
      `${quote.salaryComparison.savingsPercentage.toFixed(0)}%`
    ),
    savingsPerYear: toFormattedCurrency(quote.salaryComparison.savings.annual),
    lifeOfLeaseSavings: toFormattedCurrency(
      quote.salaryComparison.savings.total
    ),
    listPrice: toFormattedCurrency(quote.vehicle.listPrice.incGst),
    catchEQuoteId: quote.identifiers.catchEQuoteId,
    catchEQuoteUrl: quote.identifiers.catchEQuoteUrl,
    purchaseType: toFormattedValue(
      quote.vehicle.purchaseType,
      getPurchaseTypeLabel(quote.vehicle.purchaseType)
    ),
    discount: quote.vehicle.discount
      ? toFormattedCurrency(quote.vehicle.discount)
      : null,
    financeInterestRate: quote.rates.financeInterestRate
      ? toFormattedValue(
          quote.rates.financeInterestRate,
          `${quote.rates.financeInterestRate.toFixed(2)}%`
        )
      : null,
    brokerageRate: quote.rates.brokerageRate
      ? toFormattedValue(
          quote.rates.brokerageRate,
          `${quote.rates.brokerageRate.toFixed(2)}%`
        )
      : null,
    netFinanceInterestRate: quote.rates.netFinanceInterestRate
      ? toFormattedValue(
          quote.rates.netFinanceInterestRate,
          `${quote.rates.netFinanceInterestRate.toFixed(2)}%`
        )
      : null,
    netBrokerageRate: quote.rates.netBrokerageRate
      ? toFormattedValue(
          quote.rates.netBrokerageRate,
          `${quote.rates.netBrokerageRate.toFixed(2)}%`
        )
      : null,
  };
};
